import { Flags } from "@/feature-flags";
import { useFlag } from "@/hooks/use-flag";
import { useOrganization } from "@/queries/organization-queries";
import { OrganizationTier } from "gitpod-next-api/gitpod/v1/organization_pb";

export type FeatureAutomations = {
    isLoading: boolean;
    accessLevel: AutomationsAccessLevel;
    capabilities: Set<AutomationsCapabilities>;
};

export enum AutomationsAccessLevel {
    None = "none",
    Teaser = "teaser",
    Full = "full",
}

export enum AutomationsCapabilities {
    PullRequestTrigger = "pull-request-trigger",
}

export const useFeatureAutomations = (): FeatureAutomations => {
    const { data: organization, isPending: isPendingOrganization } = useOrganization();
    const { value: flagEnabled, loading: flagLoading } = useFlag(Flags.WorkflowsEnabled, false);

    if (isPendingOrganization || flagLoading) {
        return {
            accessLevel: AutomationsAccessLevel.None,
            capabilities: new Set(),
            isLoading: true,
        };
    }

    // Legacy rollout flag. Gives full access
    if (flagEnabled) {
        return {
            accessLevel: AutomationsAccessLevel.Full,
            capabilities: new Set([AutomationsCapabilities.PullRequestTrigger]),
            isLoading: false,
        };
    }

    // Access controlled by organization tier and organization configuration
    switch (organization?.tier) {
        case OrganizationTier.FREE: {
            return {
                accessLevel: AutomationsAccessLevel.None,
                capabilities: new Set(),
                isLoading: false,
            };
        }
        case OrganizationTier.FREE_ONA: {
            return {
                accessLevel: AutomationsAccessLevel.Teaser,
                capabilities: new Set(),
                isLoading: false,
            };
        }
        case OrganizationTier.CORE: {
            // TODO(PLG-launch): Should be enabled by default
            return {
                accessLevel: AutomationsAccessLevel.Teaser,
                capabilities: new Set(),
                isLoading: false,
            };
        }
        case OrganizationTier.ENTERPRISE: {
            // TODO(Enterprise rollout): Introduce logic for when to have it enabled by default for enterprise customers
            return {
                accessLevel: AutomationsAccessLevel.None,
                capabilities: new Set([AutomationsCapabilities.PullRequestTrigger]),
                isLoading: false,
            };
        }
        default: {
            return {
                accessLevel: AutomationsAccessLevel.None,
                capabilities: new Set(),
                isLoading: false,
            };
        }
    }
};
